/**
 * QuickLinksSection - Pure Presentation Component
 * 
 * ✓ STATELESS - No useState, useEffect, fetch, context, redux
 * ✓ REUSABLE - Works in Homepage, CMS, Approval Preview
 * ✓ PURE - Only receives data prop and renders
 */
function QuickLinksSection({ 
  data, 
  onNavigate
}) {
  if (!data) return null;

  // Handle both API structure (cards array), items array (from CMS), and legacy structures
  let cards = [];
  
  if (data.items && Array.isArray(data.items)) {
    cards = data.items;
  } else if (data.cards && Array.isArray(data.cards)) {
    cards = data.cards;
  } else if (data.apply_now || data.school_calendar || data.contact_us) {
    cards = [
      { id: 'apply', title: data.apply_now?.title || '', text: data.apply_now?.text || '', link: data.apply_now?.link || 'register' },
      { id: 'calendar', title: data.school_calendar?.title || '', text: data.school_calendar?.text || '', link: data.school_calendar?.link || '#school-calendar' },
      { id: 'contact', title: data.contact_us?.title || '', text: data.contact_us?.text || '', link: data.contact_us?.link || 'contact-us#contact-form' }
    ];
  } else if (data.applyNow || data.schoolCalendar || data.contactUs) {
    cards = [
      { id: 'apply', title: data.applyNow?.title || '', text: data.applyNow?.text || '', link: data.applyNow?.link || 'register' },
      { id: 'calendar', title: data.schoolCalendar?.title || '', text: data.schoolCalendar?.text || '', link: data.schoolCalendar?.link || '#school-calendar' },
      { id: 'contact', title: data.contactUs?.title || '', text: data.contactUs?.text || '', link: data.contactUs?.link || 'contact-us#contact-form' }
    ];
  }

  const kicker = data.kicker || '';
  const title = data.title || '';

  const handleCardClick = (card, idx) => {
    let target = card.link || card.target || card.url || card.redirect;
    
    // Sensible defaults if not explicitly configured in CMS
    if (!target) {
      if (card.id === 'apply' || idx === 0) target = 'register';
      else if (card.id === 'contact' || idx === 2) target = 'contact-us#contact-form';
      else target = '#school-calendar';
    }

    if (!target) return;

    // External URL (http:// or https://)
    if (target.startsWith('http://') || target.startsWith('https://')) {
      window.open(target, '_blank', 'noopener,noreferrer');
      return;
    }

    // In-page hash anchor (e.g. #school-calendar)
    if (target.startsWith('#')) {
      const el = document.querySelector(target);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
      return;
    }

    // Navigation via onNavigate (supports 'register', 'contact-us#contact-form', etc.)
    if (onNavigate) {
      onNavigate(target);
    }
  };

  const getActionLabel = (card, idx) => {
    const target = card.link || '';
    if (target.includes('register')) return 'Apply Now';
    if (target.includes('contact')) return 'Contact Us';
    if (target.includes('calendar')) return 'View Calendar';
    if (card.id === 'apply' || idx === 0) return 'Apply Now';
    if (card.id === 'contact' || idx === 2) return 'Contact Us';
    return 'Learn More';
  };

  return (
    <section className="quick-links-section" aria-label="Quick Links">
      <div className="quick-links-shell">
        <div className="quick-links-header">
          <p className="quick-links-kicker">{kicker}</p>
          <h2 className="quick-links-title">{title}</h2>
          <div className="quick-links-title-accent" aria-hidden="true"></div>
        </div>

        <div className="quick-links-grid">
          {cards.map((card, idx) => (
            <button
              key={card.id || idx}
              className={`quick-link-card button-card card-variant-${idx + 1}`}
              type="button"
              onClick={() => handleCardClick(card, idx)}
              aria-label={`${card.title}: ${card.text || card.description || ''}`}
            >
              <div className="shimmer-effect" aria-hidden="true"></div>
              
              <div className="quick-link-top-bar">
                <span className="quick-link-label">{String(idx + 1).padStart(2, '0')}</span>
                <div className="quick-link-icon" aria-hidden="true">
                  {(card.id === 'apply' || idx === 0) && (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14,2 14,8 20,8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                      <polyline points="10,9 9,9 8,9"/>
                    </svg>
                  )}
                  {(card.id === 'calendar' || idx === 1) && (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                      <line x1="16" y1="2" x2="16" y2="6"/>
                      <line x1="8" y1="2" x2="8" y2="6"/>
                      <line x1="3" y1="10" x2="21" y2="10"/>
                      <path d="M8 14h.01"/>
                      <path d="M12 14h.01"/>
                      <path d="M16 14h.01"/>
                      <path d="M8 18h.01"/>
                      <path d="M12 18h.01"/>
                    </svg>
                  )}
                  {(card.id === 'contact' || idx === 2) && (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                  )}
                </div>
              </div>

              <div className="quick-link-body">
                <h3 className="quick-link-card-title">{card.title}</h3>
                <p className="quick-link-card-desc">{card.text || card.description}</p>
              </div>

              <div className="quick-link-action-row">
                <span className="quick-link-action-badge">
                  {getActionLabel(card, idx)}
                </span>
                <span className="quick-link-action-arrow" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

export default QuickLinksSection;